// frontend/src/components/SafeHTML.js
//
// SECURITY: All HTML is sanitized with DOMPurify before rendering.
// Only formatting tags (p, strong, em, u, br, span) and the style
// attribute are allowed. This is defense-in-depth — only authenticated
// site-admins can save content to the draft JSONB.
import DOMPurify from "dompurify";

const PURIFY_CONFIG = {
  ALLOWED_TAGS: ["p", "strong", "em", "u", "br", "span"],
  ALLOWED_ATTR: ["style"],
  ALLOW_DATA_ATTR: false,
};

// SECURITY: All HTML passed to this component is sanitized with DOMPurify
// before rendering. Only formatting tags are allowed (see PURIFY_CONFIG above).
export function SafeHTML({ html, as: Tag = "span", className, style, ...rest }) {
  if (!html) return null;

  if (!/<[a-z][\s\S]*>/i.test(html)) {
    return <Tag className={className} style={style} {...rest}>{html}</Tag>;
  }

  let clean = DOMPurify.sanitize(html, PURIFY_CONFIG);

  const INLINE_OR_HEADING_TAGS = ["h1", "h2", "h3", "h4", "h5", "h6", "p", "span"];
  const tagName = typeof Tag === "string" ? Tag.toLowerCase() : "";
  const needsStrip = INLINE_OR_HEADING_TAGS.includes(tagName);

  let mergedStyle = style || {};

  if (needsStrip) {
    // Strip <p> wrappers using DOM. Hoist text-align to parent Tag so it works
    // on block elements (text-align has no effect on inline <span>).
    // SECURITY: `clean` is already sanitized by DOMPurify — safe to parse.
    const temp = document.createElement("div");
    temp.innerHTML = clean;
    const paragraphs = temp.querySelectorAll("p");
    if (paragraphs.length > 0) {
      const aligns = [...paragraphs].map((p) => p.style.textAlign || "left");
      const uniqueAligns = [...new Set(aligns)];
      if (uniqueAligns.length === 1 && uniqueAligns[0] !== "left") {
        mergedStyle = { ...mergedStyle, textAlign: uniqueAligns[0] };
      }

      const fragments = [];
      paragraphs.forEach((p) => {
        const pStyle = p.getAttribute("style");
        const nonAlignStyle = pStyle?.replace(/text-align:\s*[^;]+;?/g, "").trim();
        if (nonAlignStyle) {
          const span = document.createElement("span");
          span.setAttribute("style", nonAlignStyle);
          span.innerHTML = p.innerHTML;
          fragments.push(span.outerHTML);
        } else {
          fragments.push(p.innerHTML);
        }
      });
      clean = fragments.join("<br>");
    }
  }

  return <Tag className={className} style={mergedStyle} dangerouslySetInnerHTML={{ __html: clean }} {...rest} />;
}
